import React, { useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "./ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Card } from "./ui/card";
import { FileText, Plus, Trash2, Send, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";

const TIPOS = [
  { id: "12_meses", label: "Contrato 12 meses" },
  { id: "mensal", label: "Contrato Mensal" },
  { id: "renovacao", label: "Renovação" },
  { id: "venda", label: "Venda" },
];

const fmtMoney = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

export default function PedidoVendaForm({ lead, onCreated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    cliente_nome: lead?.empresa || lead?.nome || "",
    cliente_email: lead?.email || "",
    cliente_cpf: "",
    prazo_dias: 30,
    tipo_contrato: "12_meses",
    valor_total: 0,
    valor_primeira_fatura: 0,
    observacoes: "",
  });
  const [itens, setItens] = useState([{ nome: "", quantidade: 1, descricao: "", valor_unitario: 0 }]);

  const submitAndSend = async (enviarEmail = false) => {
    if (!form.cliente_nome.trim() || !form.cliente_email.trim()) {
      toast.error("Cliente e e-mail obrigatórios"); return;
    }
    if (form.valor_total <= 0 || form.valor_primeira_fatura <= 0) {
      toast.error("Valores devem ser positivos"); return;
    }
    setBusy(true);
    try {
      const payload = {
        lead_id: lead.id,
        ...form,
        prazo_dias: Number(form.prazo_dias),
        valor_total: Number(form.valor_total),
        valor_primeira_fatura: Number(form.valor_primeira_fatura),
        itens: itens.filter((i) => i.nome.trim()).map((i) => ({
          nome: i.nome,
          quantidade: Number(i.quantidade) || 1,
          descricao: i.descricao || null,
          valor_unitario: Number(i.valor_unitario) || 0,
        })),
      };
      const { data } = await api.post("/comercial/pedidos", payload);
      toast.success("Pedido criado!");
      if (enviarEmail) {
        const { data: env } = await api.post(`/comercial/pedidos/${data.id}/enviar`);
        toast.success(`Proposta enviada ao cliente (${env.email_mode})`);
      }
      setOpen(false);
      onCreated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao criar pedido");
    } finally { setBusy(false); }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button className="text-white" style={{ background: "#F97316" }} data-testid="novo-pedido-btn">
          <FileText size={14} className="mr-2" /> Gerar Pedido de Venda
        </Button>
      </SheetTrigger>
      <SheetContent className="sm:max-w-2xl overflow-y-auto" data-testid="pedido-drawer">
        <SheetHeader>
          <SheetTitle>Pedido de Venda — {lead?.nome}</SheetTitle>
        </SheetHeader>
        <div className="space-y-4 mt-4">
          <Card className="p-4 space-y-3">
            <h4 className="font-bold text-sm" style={{ color: "#1B3A6B" }}>Cliente</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nome / Razão Social *</Label>
                <Input data-testid="ped-cliente-nome" value={form.cliente_nome} onChange={(e) => setForm({ ...form, cliente_nome: e.target.value })} />
              </div>
              <div>
                <Label>E-mail do cliente *</Label>
                <Input data-testid="ped-cliente-email" type="email" value={form.cliente_email} onChange={(e) => setForm({ ...form, cliente_email: e.target.value })} />
              </div>
            </div>
          </Card>

          <Card className="p-4 space-y-3">
            <h4 className="font-bold text-sm" style={{ color: "#1B3A6B" }}>Condições</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo de contrato *</Label>
                <Select value={form.tipo_contrato} onValueChange={(v) => setForm({ ...form, tipo_contrato: v })}>
                  <SelectTrigger data-testid="ped-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPOS.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Prazo (dias) *</Label>
                <Input type="number" value={form.prazo_dias} onChange={(e) => setForm({ ...form, prazo_dias: e.target.value })} />
              </div>
              <div>
                <Label>Valor total (R$) *</Label>
                <Input data-testid="ped-valor-total" type="number" value={form.valor_total} onChange={(e) => setForm({ ...form, valor_total: e.target.value })} />
              </div>
              <div>
                <Label>1ª fatura (R$) *</Label>
                <Input data-testid="ped-valor-fatura" type="number" value={form.valor_primeira_fatura} onChange={(e) => setForm({ ...form, valor_primeira_fatura: e.target.value })} />
              </div>
            </div>
          </Card>

          <Card className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-bold text-sm" style={{ color: "#1B3A6B" }}>Itens / Equipamentos</h4>
              <Button size="sm" variant="outline" onClick={() => setItens([...itens, { nome: "", quantidade: 1, descricao: "", valor_unitario: 0 }])} data-testid="ped-add-item">
                <Plus size={12} className="mr-1" /> Adicionar
              </Button>
            </div>
            {itens.map((it, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`ped-item-${idx}`}>
                <div className="col-span-5">
                  {idx === 0 && <Label className="text-xs">Nome</Label>}
                  <Input value={it.nome} onChange={(e) => {
                    const c = [...itens]; c[idx].nome = e.target.value; setItens(c);
                  }} placeholder="Equipamento" />
                </div>
                <div className="col-span-2">
                  {idx === 0 && <Label className="text-xs">Qtd</Label>}
                  <Input type="number" value={it.quantidade} onChange={(e) => {
                    const c = [...itens]; c[idx].quantidade = e.target.value; setItens(c);
                  }} />
                </div>
                <div className="col-span-4">
                  {idx === 0 && <Label className="text-xs">Valor unit.</Label>}
                  <Input type="number" value={it.valor_unitario} onChange={(e) => {
                    const c = [...itens]; c[idx].valor_unitario = e.target.value; setItens(c);
                  }} />
                </div>
                <div className="col-span-1">
                  {itens.length > 1 && (
                    <Button size="sm" variant="ghost" onClick={() => setItens(itens.filter((_, i) => i !== idx))}>
                      <Trash2 size={14} className="text-red-600" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            <div className="text-right text-sm text-muted-foreground">
              Subtotal itens: <strong>{fmtMoney(itens.reduce((s, i) => s + (Number(i.quantidade) || 0) * (Number(i.valor_unitario) || 0), 0))}</strong>
            </div>
          </Card>

          <div>
            <Label>Observações</Label>
            <Textarea value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
          </div>

          <div className="flex gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => submitAndSend(false)} disabled={busy} className="flex-1" data-testid="ped-salvar-rascunho">
              {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : null} Salvar Rascunho
            </Button>
            <Button onClick={() => submitAndSend(true)} disabled={busy} className="flex-1 text-white" style={{ background: "#1B3A6B" }} data-testid="ped-salvar-enviar">
              {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Mail size={14} className="mr-2" />} Salvar e Enviar ao Cliente
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
